import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, ArrowRight, Loader2, Lock, Mail, Moon, Sun } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useAdminMode } from '../../components/admin/theme/hooks';
import '../../components/admin/theme/admin-theme.css';

const inputCls =
  'h-12 w-full rounded-[10px] border border-[color:var(--oa-line-2)] bg-[color:var(--oa-raised)] pl-11 pr-4 text-[14px] text-[color:var(--oa-text)] outline-none transition-[border-color,box-shadow] duration-200 placeholder:text-[color:var(--oa-faint)] hover:border-[color:var(--oa-line-3)] focus:border-[color:var(--oa-accent)] focus:shadow-[0_0_0_4px_var(--oa-accent-soft)] disabled:opacity-60';

// Admin sign-in, in the admin shell's look: drifting backdrop, one glass card, the saved
// light/dark mode. Behaviour is unchanged: AuthContext.signIn, then /admin.
const AdminLogin: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { signIn } = useAuth();
  const { mode, toggle } = useAdminMode();

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await signIn(email, password);

    if (result.success) {
      navigate('/admin');
    } else {
      setError(result.error || 'Invalid email or password');
    }

    setLoading(false);
  };

  return (
    <div className="oa-root relative flex min-h-screen items-center justify-center overflow-hidden bg-[color:var(--oa-ground)] px-4 py-10">
      <div className="oa-backdrop" aria-hidden="true">
        <i className="oa-blob oa-b1" />
        <i className="oa-blob oa-b2" />
        <i className="oa-blob oa-b3" />
      </div>

      <button type="button" onClick={toggle} className="oa-icon-btn absolute right-4 top-4 z-10" aria-label={mode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}>
        {mode === 'dark' ? <Sun /> : <Moon />}
      </button>

      <main className="relative z-10 w-full max-w-[400px]">
        <div
          className="oa-card gap-0 border-[color:var(--oa-line-2)] p-8 md:p-9"
          style={{ background: 'color-mix(in oklab, var(--oa-card) 86%, transparent)', backdropFilter: 'blur(20px) saturate(1.2)', WebkitBackdropFilter: 'blur(20px) saturate(1.2)', boxShadow: 'var(--oa-shadow)' }}
        >
          <div className="flex items-center gap-2.5">
            <span className="grid h-9 w-9 place-items-center rounded-[10px] bg-[color:var(--oa-accent)] font-montserrat text-[13px] font-extrabold tracking-[-0.04em] text-[color:var(--oa-on-accent)] shadow-[0_10px_26px_-12px_var(--oa-accent)]">OC</span>
            <span className="font-montserrat text-[18px] font-bold tracking-[-0.02em]">Outpost</span>
            <span className="rounded-md border border-[color:var(--oa-line-2)] px-1.5 py-px text-[10px] font-semibold uppercase tracking-[0.08em] text-[color:var(--oa-faint)]">Admin</span>
          </div>

          <h1 className="mt-7 font-montserrat text-[26px] font-extrabold leading-tight tracking-[-0.03em]">Sign in</h1>
          <p className="oa-cap mt-1.5">Chats, the catalogue, the blog and site figures, in one place.</p>

          <form onSubmit={handleLogin} className="mt-7 flex flex-col gap-4">
            {error ? (
              <div role="alert" className="flex items-center gap-2.5 rounded-[10px] bg-[color:color-mix(in_oklab,var(--oa-bad)_12%,transparent)] px-3.5 py-3 text-[13px] text-[color:var(--oa-bad)]" style={{ animation: 'oa-rowin .4s var(--oa-ease) both' }}>
                <AlertCircle className="h-4 w-4 shrink-0" />
                <span>{error}</span>
              </div>
            ) : null}

            <label className="flex flex-col gap-1.5">
              <span className="oa-sub">Email</span>
              <span className="relative">
                <Mail className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[color:var(--oa-faint)]" />
                <input type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} placeholder="you@outpostcustom.co.uk" required disabled={loading} />
              </span>
            </label>

            <label className="flex flex-col gap-1.5">
              <span className="oa-sub">Password</span>
              <span className="relative">
                <Lock className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[color:var(--oa-faint)]" />
                <input type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} placeholder="Your password" required disabled={loading} />
              </span>
            </label>

            <button type="submit" disabled={loading} className="oa-accent-btn group relative mt-2 h-12 w-full justify-center overflow-hidden text-[14px] disabled:opacity-70">
              {/* Sheen sweeps across on hover, as on the dashboard's split bar. */}
              <span className="pointer-events-none absolute inset-0 -translate-x-full bg-[linear-gradient(100deg,transparent_30%,rgba(255,255,255,0.45)_50%,transparent_70%)] transition-transform duration-700 group-hover:translate-x-full" />
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> Signing in…
                </>
              ) : (
                <>
                  Sign in <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </>
              )}
            </button>
          </form>

          <p className="mt-7 text-center text-[12px] text-[color:var(--oa-faint)]">Locked out? Ask Alastair to reset your password.</p>
        </div>
      </main>
    </div>
  );
};

export default AdminLogin;
